import { ColumnsType } from 'antd/es/table'
import { SortOrder } from 'antd/es/table/interface'
import { Status, USER_STATUS_TYPES_TO_CLASSNAME } from 'components/Status'
import { Dropdown } from 'components/Dropdown'
import { Sorter } from 'components/Sorter/Sorter'
import { fetchHealthSystemsList } from 'features/HealthSystems/api'
import {
  USER_STATUSES_OPTIONS,
  COMMUNITY_ROLES_OPTIONS,
  ROLES_NAMES,
  COMMONS_ROLES_OPTIONS,
  VENDOR_ROLES_OPTIONS
} from 'constants/common'
import { ReactComponent as Filter16 } from 'assets/svg/Filter16.svg'
import { ReactComponent as FilterApplied16 } from 'assets/svg/FilterApplied16.svg'
import {
  TRFPUsersData,
  TRFPUsersFilters,
  TRFPUsersFiltersFunc,
  TRFPUsersColumnsFunc
} from './types'
import { fullFormatPhoneNumber } from 'helper/common'

import { fetchContractCategories } from '../../../pages/CRUDUser/api'
import { DescriptionCell } from '../../../components/DescriptionCell/DescriptionCell'
import { Chips } from 'components/Chips/Chips'
import { Tooltip } from 'components/Tooltip/Tooltip'

export const columns = ({
  applyFilters,
  filters,
  community,
  isVendor = false,
  isVendorTab,
  canSeeHealthSystem
}: TRFPUsersColumnsFunc): ColumnsType<TRFPUsersData> => {
  const _columns: ColumnsType<TRFPUsersData> = [
    {
      dataIndex: 'first_name',
      key: 'first_name',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TRFPUsersData>
          dataIndex="first_name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          User name
        </Sorter>
      ),
      width: 180,
      render: (
        _,
        {
          first_name,
          last_name,
          is_voting_panel_member,
          voting_panel_category_names
        }
      ) => (
        <div className="flex column">
          <div>
            {first_name} {last_name}
          </div>
          {is_voting_panel_member && (
            <div className="fit-content">
              <Tooltip
                title={`Voting panel for ${voting_panel_category_names?.join(
                  ', '
                )}`}
              >
                <Chips closable={false} grey>
                  Voting panel
                </Chips>
              </Tooltip>
            </div>
          )}
        </div>
      ),
      sorter: true,
      showSorterTooltip: false
    },
    {
      dataIndex: 'role',
      key: 'role',
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TRFPUsersData>
          dataIndex="role"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Role
        </Sorter>
      ),
      width: 160,
      filterDropdown: (props) => (
        <Dropdown.Filter<TRFPUsersFilters, TRFPUsersFiltersFunc, unknown>
          items={
            isVendorTab
              ? VENDOR_ROLES_OPTIONS
              : [...COMMUNITY_ROLES_OPTIONS, ...COMMONS_ROLES_OPTIONS]
          }
          applyFilters={applyFilters}
          appliedFilters={filters}
          field="roles"
          {...props}
        />
      ),
      render: (text) => ROLES_NAMES[text],
      filterIcon: !!filters?.roles?.length ? <FilterApplied16 /> : <Filter16 />,
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    }
  ]

  _columns.push({
    dataIndex: 'status',
    key: 'status',
    width: 130,
    title: ({ sortOrder, sortColumn }) => (
      <Sorter<TRFPUsersData>
        dataIndex="status"
        sortColumn={sortColumn}
        sortOrder={sortOrder as SortOrder}
      >
        Status
      </Sorter>
    ),
    filterDropdown: (props) => (
      <Dropdown.Filter<TRFPUsersFilters, TRFPUsersFiltersFunc, unknown>
        items={USER_STATUSES_OPTIONS.filter(
          (item) => item.value !== 'Archived'
        )}
        applyFilters={applyFilters}
        appliedFilters={filters}
        field="statuses"
        {...props}
      />
    ),
    render: (_, { status }) => (
      <Status.Default className={USER_STATUS_TYPES_TO_CLASSNAME[status]}>
        {status}
      </Status.Default>
    ),
    filterIcon: !!filters?.statuses?.length ? (
      <FilterApplied16 />
    ) : (
      <Filter16 />
    ),
    filterSearch: true,
    sorter: true,
    showSorterTooltip: false
  })

  _columns.push({
    dataIndex: 'email',
    key: 'email',
    width: 160,
    title: ({ sortOrder, sortColumn }) => (
      <Sorter<TRFPUsersData>
        dataIndex="email"
        sortColumn={sortColumn}
        sortOrder={sortOrder as SortOrder}
      >
        Email
      </Sorter>
    ),
    sorter: true,
    showSorterTooltip: false
  })
  if (!isVendor)
    _columns.push({
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 170,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TRFPUsersData>
          dataIndex="phone_number"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Phone #
        </Sorter>
      ),
      render: (_, { phone_number }) => fullFormatPhoneNumber(phone_number),
      sorter: true,
      showSorterTooltip: false
    })

  if (!isVendorTab) {
    if (canSeeHealthSystem) {
      _columns.push({
        dataIndex: 'health_system',
        key: 'health_system',
        width: 170,
        title: ({ sortOrder, sortColumn }) => (
          <Sorter<TRFPUsersData>
            dataIndex="health_system"
            sortColumn={sortColumn}
            sortOrder={sortOrder as SortOrder}
          >
            Health system
          </Sorter>
        ),
        filterDropdown: (props) => (
          <Dropdown.Filter<TRFPUsersFilters, TRFPUsersFiltersFunc, unknown>
            applyFilters={applyFilters}
            appliedFilters={filters}
            searchCallback={() => fetchHealthSystemsList(community, {})}
            field="health_systems"
            asyncSearch
            {...props}
          />
        ),
        filterIcon: !!filters?.health_systems?.length ? (
          <FilterApplied16 />
        ) : (
          <Filter16 />
        ),
        filterSearch: true,
        sorter: true,
        showSorterTooltip: false
      })
    }
    _columns.push({
      dataIndex: 'hospital',
      key: 'hospital',
      width: 180,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter<TRFPUsersData>
          dataIndex="hospital"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Hospital
        </Sorter>
      ),
      filterIcon: !!filters?.hospitals?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    })
  } else {
    _columns.push({
      dataIndex: 'contract_categories_names',
      key: 'contract_categories_names',
      width: 240,
      title: 'Contract category',
      filterDropdown: (props) => (
        <Dropdown.Filter<TRFPUsersFilters, TRFPUsersFiltersFunc, unknown>
          applyFilters={applyFilters}
          appliedFilters={filters}
          searchCallback={fetchContractCategories}
          field="contract_category"
          asyncSearch
          {...props}
        />
      ),
      render: (_, { contract_categories_names }) => (
        <DescriptionCell
          content={contract_categories_names}
          text={contract_categories_names}
        />
      ),
      filterIcon: !!filters?.contract_category?.length ? (
        <FilterApplied16 />
      ) : (
        <Filter16 />
      ),
      filterSearch: true,
      sorter: true,
      showSorterTooltip: false
    })
  }

  return _columns
}
